import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable, timeout } from 'rxjs';
import { map } from 'rxjs/operators';

/** Tenant flag that opens new claims in the new claims flow. */
export const NEW_CLAIMS_FLOW_FLAG = 'NEW_CLAIMS_FLOW';

interface FeatureFlagsResponse {
  feature_flags?: Record<string, boolean>;
}

type FeatureFlagsLoadState = 'loading' | 'loaded' | 'error';
const FEATURE_FLAGS_REQUEST_TIMEOUT_MS = 20_000;

/**
 * Centralized service for managing feature flags.
 * Fetches flags once at initialization and caches them.
 * Call refresh() after updating flags to reload
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private flags$ = new BehaviorSubject<Record<string, boolean> | null>(null);
  private loadStateSubject = new BehaviorSubject<FeatureFlagsLoadState>('loading');
  readonly loadState$ = this.loadStateSubject.asObservable();
  private loading = false;

  constructor(private http: HttpClient) {
    this.loadFlags();
  }

  /**
   * Load feature flags from the API
   */
  private loadFlags(): void {
    if (this.loading) return;

    this.loading = true;
    this.loadStateSubject.next('loading');
    this.flags$.next(null);
    this.http
      .get<FeatureFlagsResponse>('/api/feature-flags')
      .pipe(timeout({ first: FEATURE_FLAGS_REQUEST_TIMEOUT_MS }))
      .subscribe({
        next: response => {
          this.flags$.next(response?.feature_flags ?? {});
          this.loading = false;
          this.loadStateSubject.next('loaded');
        },
        error: () => {
          this.flags$.next(null);
          this.loading = false;
          this.loadStateSubject.next('error');
        },
      });
  }

  //   Check if a specific feature flag is enabled

  isEnabled(flagName: string): Observable<boolean> {
    return this.getAll().pipe(map(flags => Boolean(flags[flagName])));
  }

  /** Preserve loading and request failures for checkout decisions that must fail closed. */
  isEnabledState(flagName: string): Observable<boolean | null> {
    return this.flags$.pipe(map(flags => (flags === null ? null : Boolean(flags[flagName]))));
  }

  /**
   * Refresh feature flags from the server
    after updating flags to get the latest values
   */
  refresh(): void {
    this.loadFlags();
  }

  /**
   * Get all feature flags as an observable
   */
  getAll(): Observable<Record<string, boolean>> {
    return this.flags$.pipe(filter((flags): flags is Record<string, boolean> => flags !== null));
  }
}
